import {
  Handle,
  NodeProps,
  Node,
  NodeToolbar,
  Position,
  useReactFlow,
} from "@xyflow/react";
import { JSX, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { InteractionType } from "../../models/interactions/Interaction";
import { IIf } from "../../models/interactions/If";
import { IMath } from "../../models/interactions/Math";
import { getInteractionFromFactory } from "../interaction_factory/InteractionFactory";
import {
  selectErrors,
  selectErrorsForInteraction,
} from "../../app/slices/ValidationSlice";
import { RootState } from "../../app/Store";
import classNames from "classnames";
import IfOperatorDropdown from "./components/IfOperatorDropdown";
import MathOperatorDropdown from "./components/MathOperatorDropdown";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import { getHandleStyle, isHandleConnected } from "./SequenceNodeHandles";
import {
  HandleConfig,
  HandleType,
  SourceHandleConfig,
  TARGET_HANDLE_CONFIG,
  getSourceHandleConfigs,
} from "./SequenceNodeHandleConfigs";
import type { SequenceNodeData } from "../../models/interactions/SequenceModel";
import {
  ITrigger,
  TriggerType,
  getTriggerLabel,
} from "../../models/interactions/Trigger";
import { getSequenceNodeTitle } from "./SequenceNodeTitle";
import { useSequenceCanvasLod } from "./SequenceCanvasLod";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";
import { selectSelectedElementType } from "../../app/slices/TrainingSlice";
import { isTakeSequenceEditor } from "./takeSequenceEditor";
import TakeStartSetup from "./take_setup/TakeStartSetup";
import TakeEndHandover from "./take_setup/TakeEndHandover";

export type { SequenceNodeData } from "../../models/interactions/SequenceModel";

export type SequenceNodeType = Node<SequenceNodeData, "sequence">;

function getTriggerVisualConfig(triggerType: TriggerType) {
  if (triggerType === TriggerType.OnStart) {
    return {
      label: getTriggerLabel(triggerType),
      textClassName: "text-interaction-correct",
      Icon: PlayArrowIcon,
    };
  }

  return {
    label: getTriggerLabel(triggerType),
    textClassName: "text-creator-error",
    Icon: StopIcon,
  };
}

export function SequenceNode({
  id,
  data,
  selected,
}: NodeProps<SequenceNodeType>): JSX.Element {
  const customColor = data.color;
  const interactionType = data.interaction.type;
  const lod = useSequenceCanvasLod();
  const isFullLod = lod === "full";
  const isOverviewLod = lod === "overview";
  const { getEdges } = useReactFlow();
  const edges = getEdges();
  const copyPasteContext = useContext(CopyPasteContext);
  const pendingSelection =
    copyPasteContext?.pendingCut?.kind === "sequenceSelection"
      ? copyPasteContext.pendingCut
      : null;
  const isPendingCut = Boolean(
    pendingSelection &&
      pendingSelection.source.sequenceId === data.parentSequenceId &&
      pendingSelection.selection.nodeIds.includes(id),
  );

  const errors = useSelector((state: RootState) => {
    const parentSequenceId = data.parentSequenceId;
    if (parentSequenceId) {
      const allErrors = selectErrors(state);
      if (Object.prototype.hasOwnProperty.call(allErrors, parentSequenceId)) {
        return (allErrors[parentSequenceId] ?? []).filter(
          (error) => error.nodeId === id,
        );
      }
    }

    return selectErrorsForInteraction(state, data.interaction.id);
  });
  const hasErrors = (errors?.length ?? 0) > 0;
  const triggerVisualConfig =
    interactionType === InteractionType.Trigger
      ? getTriggerVisualConfig((data.interaction as ITrigger).triggerType)
      : null;
  const TriggerHeaderIcon = triggerVisualConfig?.Icon;
  const isTriggerNode = interactionType === InteractionType.Trigger;
  const triggerType = isTriggerNode
    ? (data.interaction as ITrigger).triggerType
    : undefined;
  const isOnTakeStart = triggerType === TriggerType.OnStart;
  const isOnTakeEnd = triggerType === TriggerType.OnEnd;
  // In a take's sequence, On Take Start shows the take's own setup and On Take End
  // the handover to the next take, each with the trigger's output inside.
  const elementType = useSelector((state: RootState) =>
    isOnTakeStart || isOnTakeEnd ? selectSelectedElementType(state) : undefined,
  );
  const inTakeSequence = isTakeSequenceEditor(elementType);
  const showsTakeSetup = isOnTakeStart && inTakeSequence;
  const showsTakeEnd = isOnTakeEnd && inTakeSequence;
  const showsTakeFlow = showsTakeSetup || showsTakeEnd;

  const content = useMemo(() => {
    return getInteractionFromFactory(data.interaction.type).NodeView?.(
      id,
      data.interaction,
    );
  }, [data.interaction, id, interactionType]);

  const sourceHandleConfigs = getSourceHandleConfigs(interactionType);
  const hasTargetHandle =
    interactionType !== InteractionType.Sequence &&
    interactionType !== InteractionType.SubSequence &&
    interactionType !== InteractionType.Trigger;

  const renderConnectedHandle = (
    config: HandleConfig,
    handleType: HandleType,
    key?: string,
  ): JSX.Element => {
    const handleId = config.id;
    const handleStyle = getHandleStyle(
      isHandleConnected(id, handleId, handleType, edges),
      selected,
      customColor,
      config.variant,
      config.extraStyle,
      config.colorOverride,
    );

    return (
      <Handle
        key={key}
        id={handleId}
        style={{
          ...handleStyle,
          ...(isOverviewLod
            ? { opacity: 0, pointerEvents: "none" as const }
            : {}),
        }}
        type={handleType}
        position={config.position}
      />
    );
  };

  const renderWrappedHandle = (
    config: SourceHandleConfig,
    index: number,
  ): JSX.Element => {
    const key = config.id ?? `source-${index}`;
    if (!config.wrapperStyle) {
      return renderConnectedHandle(config, "source", key);
    }

    return (
      <div key={key} className="absolute" style={config.wrapperStyle}>
        {renderConnectedHandle(config, "source")}
      </div>
    );
  };

  const borderStyle =
    customColor !== undefined ? { borderColor: customColor } : undefined;
  const restingBorderStyle =
    !customColor && !hasErrors && !selected
      ? { borderColor: "color-mix(in srgb, #3AC4FF 45%, var(--pi-surface))" }
      : undefined;
  const nodeTitle = getSequenceNodeTitle(data.interaction);
  const titleClassName =
    lod === "full"
      ? "text-[15px] font-medium text-creator-text-sub"
      : "text-[20px] font-medium text-creator-text-sub";
  const indicatorClassName = classNames(
    "flex-shrink-0",
    lod === "compact" && "scale-110",
    isOverviewLod && "scale-[1.4]",
  );
  const overviewLabelBorderStyle =
    !hasErrors && customColor ? { borderColor: customColor } : undefined;

  // Ring style for selected nodes with custom colors
  const customSelectionStyle =
    selected && !hasErrors && customColor
      ? {
          borderColor: customColor,
          boxShadow: `0 0 0 0.5px ${customColor}, 0 0 0 3px ${customColor}40, 0 4px 20px ${customColor}66`,
        }
      : undefined;

  return (
    <div
      className={classNames(
        "flex relative flex-col border-solid [&_.pi-creator-dropdown-label]:text-creator-text-sub px-[16px] py-[8px] rounded-[8px] transition-[background-color,border-color,box-shadow,opacity] duration-150 motion-reduce:transition-none",
        // The take trigger nodes size to their content, which keeps both the same width.
        showsTakeFlow ? "w-fit min-w-[400px]" : "w-[256px]",
        isPendingCut && "opacity-60",
        isTriggerNode ? "gap-y-[6px] min-h-[92px]" : "gap-y-[12px]",
        hasErrors
          ? "bg-creator-error-washed border-creator-error shadow-[0_4px_14px_rgba(211,95,95,0.3)]"
          : "bg-creator-surface",
        selected &&
          hasErrors &&
          "ring-2 ring-creator-primary-hover ring-offset-2 ring-offset-creator-surface",
        // Single node selection styling - make it more prominent (only for non-custom colors)
        // One border width in every state, so selecting a node never shifts its content
        // (clicking a dropdown on an unselected node used to jump half a pixel).
        // Resting: a muted blue outline (style below). Selected: full blue, the extra
        // half pixel of outline drawn as a shadow, plus the soft ring.
        "border-[1.5px]",
        selected && !hasErrors && !customColor
          ? "border-creator-primary shadow-[0_0_0_0.5px_#3AC4FF,0_0_0_4px_rgba(58,196,255,0.15),0_4px_18px_rgba(58,196,255,0.25)]"
          : !customColor &&
              !hasErrors &&
              "border-creator-primary shadow-[0_4px_14px_rgba(58,196,255,0.1)]",
      )}
      style={customSelectionStyle ?? borderStyle ?? restingBorderStyle}
      aria-label={
        isPendingCut
          ? `${nodeTitle}. Cut pending. Paste to move or press Escape to cancel.`
          : undefined
      }
    >
      {isPendingCut && (
        <span
          className="pointer-events-none absolute -top-3 right-2 z-20 flex h-6 w-6 items-center justify-center rounded-[6px] border border-creator-primary bg-creator-surface text-[16px] text-creator-text-main"
          aria-label="Cut pending. Paste to move or press Escape to cancel."
        >
          <ContentCutOutlinedIcon fontSize="inherit" aria-hidden="true" />
        </span>
      )}
      <style>{`
        .react-flow__handle { transition: transform 150ms ease; }
        .react-flow__handle-left:hover { transform: translate(-50%, -50%) scale(1.3) !important; }
        .react-flow__handle-right:hover { transform: translate(50%, -50%) scale(1.3) !important; }
        .react-flow__handle-bottom:hover { transform: translate(-50%, 50%) scale(1.3) !important; }
        .react-flow__handle-top:hover { transform: translate(-50%, -50%) scale(1.3) !important; }
      `}</style>
      <NodeToolbar
        isVisible={isOverviewLod}
        position={Position.Top}
        offset={6}
        align="center"
        className="pointer-events-none select-none"
      >
        <div
          className={classNames(
            "flex max-w-[200px] items-center gap-[6px] rounded-[6px] border bg-creator-surface px-[8px] py-[4px] text-[14px] font-semibold leading-[20px] text-creator-text shadow-[0_2px_8px_rgba(0,0,0,0.18)]",
            hasErrors ? "border-creator-error" : "border-creator-primary",
          )}
          style={overviewLabelBorderStyle}
          data-testid="sequence-node-overview-label"
        >
          {hasErrors && (
            <ErrorIcon
              className="flex-shrink-0 text-creator-error"
              fontSize="inherit"
            />
          )}
          {interactionType === InteractionType.Trigger && triggerVisualConfig ? (
            <>
              {TriggerHeaderIcon && (
                <TriggerHeaderIcon
                  fontSize="inherit"
                  className={classNames(
                    "flex-shrink-0",
                    triggerVisualConfig.textClassName,
                  )}
                />
              )}
              <span className="truncate" title={triggerVisualConfig.label}>
                {triggerVisualConfig.label}
              </span>
            </>
          ) : (
            <span className="truncate" title={nodeTitle}>
              {nodeTitle}
            </span>
          )}
        </div>
      </NodeToolbar>
      {hasTargetHandle && renderConnectedHandle(TARGET_HANDLE_CONFIG, "target")}
      {/* Draggable header */}
      <div
        className={classNames(
          "relative z-10 cursor-move draggable-header flex items-center justify-between gap-[8px] leading-[24px]",
          titleClassName,
          isOverviewLod && "invisible pointer-events-none select-none",
        )}
        aria-hidden={isOverviewLod}
      >
        <div className="flex items-center gap-[8px] flex-1 min-w-0">
          {hasErrors && (
            <ErrorIcon
              className={classNames(
                indicatorClassName,
                "text-creator-error",
              )}
              fontSize="small"
            />
          )}
          {interactionType === InteractionType.Trigger && triggerVisualConfig ? (
            <div className="flex items-center gap-[8px] min-w-0">
              {TriggerHeaderIcon && (
                <TriggerHeaderIcon
                  fontSize="small"
                  className={classNames(
                    indicatorClassName,
                    triggerVisualConfig.textClassName,
                  )}
                />
              )}
              <span
                className="truncate text-creator-text-sub"
                title={triggerVisualConfig.label}
              >
                {triggerVisualConfig.label}
              </span>
            </div>
          ) : (
            <span className="truncate" title={nodeTitle}>
              {nodeTitle}
            </span>
          )}
        </div>
        {interactionType === InteractionType.If && (
          <div
            className={classNames(
              !isFullLod && "invisible pointer-events-none",
            )}
            aria-hidden={!isFullLod}
          >
            <IfOperatorDropdown
              nodeId={id}
              interaction={data.interaction as IIf}
            />
          </div>
        )}
        {interactionType === InteractionType.Math && (
          <div
            className={classNames(
              !isFullLod && "invisible pointer-events-none",
            )}
            aria-hidden={!isFullLod}
          >
            <MathOperatorDropdown
              nodeId={id}
              interaction={data.interaction as IMath}
            />
          </div>
        )}
      </div>
      {/* Non-draggable content */}
      {content && !isTriggerNode && (
        <div
          className={classNames(
            "cursor-default noDrag",
            !isFullLod && "invisible pointer-events-none select-none",
          )}
          aria-hidden={!isFullLod}
          onScroll={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      )}
      {showsTakeFlow && (
        <div
          className={classNames(
            "cursor-default noDrag",
            !isFullLod && "invisible pointer-events-none select-none",
          )}
          aria-hidden={!isFullLod}
        >
          {showsTakeSetup ? (
            <TakeStartSetup
              triggerLabel={triggerVisualConfig?.label ?? ""}
              handle={renderConnectedHandle(
                { ...sourceHandleConfigs[0], extraStyle: undefined },
                "source",
              )}
            />
          ) : (
            <TakeEndHandover
              handle={renderConnectedHandle(
                { ...sourceHandleConfigs[0], extraStyle: undefined },
                "source",
              )}
            />
          )}
        </div>
      )}
      {content && isTriggerNode && !showsTakeFlow && (
        <div
          className={classNames(
            "flex flex-1 items-center pr-[24px] cursor-default noDrag",
            !isFullLod && "invisible pointer-events-none select-none",
          )}
          aria-hidden={!isFullLod}
          onScroll={(e) => e.stopPropagation()}
        >
          {content}
        </div>
      )}
      {!showsTakeFlow &&
        sourceHandleConfigs.map((config, index) =>
          renderWrappedHandle(config, index),
        )}
    </div>
  );
}
